/**
 * Dialog for creating or editing a canonical composer.
 * In create mode (no composer prop), creates a new canonical composer.
 * In edit mode (composer prop provided), updates an existing canonical composer.
 */

import { useState, useEffect } from 'react'
import {
  useCreateCanonicalComposer,
  useUpdateCanonicalComposer,
} from '../../hooks/useCanonicalComposers'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@brent/core-ui/ui'
import { Button } from '@brent/core-ui/ui'
import { Label } from '@brent/core-ui/ui'
import { Input } from '@brent/core-ui/ui'
import { Textarea } from '@brent/core-ui/ui'
import { Loader2 } from 'lucide-react'
import { useToast } from '../../hooks/use-toast'
import type { CanonicalComposer } from '../../types/canonical-composer'

interface EditCanonicalComposerDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  composer?: CanonicalComposer
}

export function EditCanonicalComposerDialog({
  open,
  onOpenChange,
  composer,
}: EditCanonicalComposerDialogProps) {
  const createMutation = useCreateCanonicalComposer()
  const updateMutation = useUpdateCanonicalComposer()
  const { toast } = useToast()

  const isEditMode = !!composer

  // Form state
  const [name, setName] = useState('')
  const [birthYear, setBirthYear] = useState('')
  const [deathYear, setDeathYear] = useState('')
  const [notes, setNotes] = useState('')

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setName(composer?.name ?? '')
      setBirthYear(composer?.birth_year?.toString() ?? '')
      setDeathYear(composer?.death_year?.toString() ?? '')
      setNotes(composer?.notes ?? '')
    }
  }, [open, composer])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!name.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Name is required',
        variant: 'destructive',
      })
      return
    }

    const parsedBirthYear = birthYear.trim() ? parseInt(birthYear.trim(), 10) : null
    const parsedDeathYear = deathYear.trim() ? parseInt(deathYear.trim(), 10) : null

    if (birthYear.trim() && isNaN(parsedBirthYear!)) {
      toast({
        title: 'Validation Error',
        description: 'Birth year must be a valid number',
        variant: 'destructive',
      })
      return
    }

    if (deathYear.trim() && isNaN(parsedDeathYear!)) {
      toast({
        title: 'Validation Error',
        description: 'Death year must be a valid number',
        variant: 'destructive',
      })
      return
    }

    try {
      if (isEditMode) {
        await updateMutation.mutateAsync({
          composerId: composer.canonical_composer_id,
          input: {
            name: name.trim(),
            birth_year: parsedBirthYear,
            death_year: parsedDeathYear,
            notes: notes.trim() || null,
          },
        })

        toast({
          title: 'Success',
          description: 'Canonical composer updated successfully',
        })
      } else {
        await createMutation.mutateAsync({
          name: name.trim(),
          birth_year: parsedBirthYear,
          death_year: parsedDeathYear,
          notes: notes.trim() || null,
        })

        toast({
          title: 'Success',
          description: 'Canonical composer created successfully',
        })
      }

      onOpenChange(false)
    } catch (error) {
      toast({
        title: 'Error',
        description:
          error instanceof Error
            ? error.message
            : `Failed to ${isEditMode ? 'update' : 'create'} canonical composer`,
        variant: 'destructive',
      })
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? 'Edit Canonical Composer' : 'Create New Composer'}
          </DialogTitle>
          <DialogDescription>
            {isEditMode
              ? 'Update the details for this canonical composer.'
              : 'Create a new canonical composer for pieces not found in Wikidata.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="composer-name">Name *</Label>
            <Input
              id="composer-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. John Smith"
              disabled={isPending}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="birth-year">Birth Year</Label>
              <Input
                id="birth-year"
                type="number"
                value={birthYear}
                onChange={(e) => setBirthYear(e.target.value)}
                placeholder="e.g. 1860"
                disabled={isPending}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="death-year">Death Year</Label>
              <Input
                id="death-year"
                type="number"
                value={deathYear}
                onChange={(e) => setDeathYear(e.target.value)}
                placeholder="e.g. 1911"
                disabled={isPending}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes about this composer..."
              rows={3}
              disabled={isPending}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditMode ? 'Save Changes' : 'Create Composer'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
